import React, { useEffect, useState } from "react";
import "./DirectoryDrawer.css";

type DrawerSize = "default" | "wide" | "form";
type DrawerPlacement = "side" | "center";

type Props = {
  open: boolean;
  title: string;
  onClose: () => void;
  size?: DrawerSize;
  placement?: DrawerPlacement;
  children: React.ReactNode;
};

// Keeps the drawer mounted long enough to play its exit transition instead of
// vanishing instantly, and defers the "open" class a frame so the enter
// transition has a closed starting frame to animate from.
const DRAWER_EXIT_MS = 220;

export const DirectoryDrawer: React.FC<Props> = ({
  open,
  title,
  onClose,
  size = "default",
  placement = "side",
  children,
}) => {
  const [shouldRender, setShouldRender] = useState(open);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let raf1 = 0;
    let raf2 = 0;
    let closeTimer: ReturnType<typeof setTimeout> | undefined;

    if (open) {
      setShouldRender(true);
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setVisible(true));
      });
    } else {
      setVisible(false);
      closeTimer = setTimeout(() => setShouldRender(false), DRAWER_EXIT_MS);
    }

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      if (closeTimer) clearTimeout(closeTimer);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!shouldRender) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [shouldRender]);

  if (!shouldRender) return null;

  return (
    <div
      className={`directory-drawer__backdrop${placement === "center" ? " directory-drawer__backdrop--center" : ""}${visible ? " is-open" : ""}`}
      role="presentation"
      onClick={onClose}
    >
      <aside
        className={`directory-drawer directory-drawer--${size}${placement === "center" ? " directory-drawer--center" : ""}${visible ? " is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="directory-drawer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="directory-drawer__head">
          <h2 id="directory-drawer-title" className="directory-drawer__title">
            {title}
          </h2>
          <button type="button" className="directory-drawer__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <div className="directory-drawer__body">{children}</div>
      </aside>
    </div>
  );
};
